import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { OrganizationUser } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import Alert from "components/Alert";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

type Provider = "openai" | "google" | "anthropic";

type Stats = {
  period: string;
  activity: {
    outreachEmailsSent: number;
    followUpEmailsSent: number;
    openRate: number;
    responseRate: number;
    responsesReceived: number;
    positiveResponses: number;
    negativeResponses: number;
    neutralResponses: number;
  };
  templates: {
    name: string;
    sent: number;
    response_rate: number;
    days_since_update: number | null;
  }[];
  campaigns: {
    name: string;
    status: string;
    start_date: string | null;
    end_date: string | null;
    emails_sent: number;
  }[];
  monthlyLimit: number | null;
  maxFollowupSteps: number;
  outreachTemplateCount: number;
  pendingBrandResponseCount: number;
  pendingRateDiscussionCount: number;
  totalPendingActionItems: number;
};

type PlanStep = {
  type: string;
  action: string;
  detail: string;
};

const PERIOD_OPTIONS = [
  { value: 1, label: "Last 30 days" },
  { value: 2, label: "30–60 days ago" },
  { value: 3, label: "60–90 days ago" },
];

type ModelOption = { id: string; note: string };

const MODEL_OPTIONS: Record<Provider, ModelOption[]> = {
  openai: [
    { id: "gpt-5.4-nano", note: "Fast & cheap — good for most experiments" },
    { id: "gpt-5.4-mini", note: "Balanced quality and cost" },
    { id: "gpt-5.4", note: "Best reasoning, highest cost" },
  ],
  google: [
    {
      id: "gemini-2.5-flash-lite",
      note: "Cheapest — good for most experiments",
    },
    {
      id: "gemini-2.5-flash",
      note: "Balanced quality and cost",
    },
    { id: "gemini-2.5-pro", note: "Best reasoning, higher cost" },
  ],
  anthropic: [
    {
      id: "claude-haiku-4-5-20251001",
      note: "Fast & cheap — good for most experiments",
    },
    { id: "claude-sonnet-4-6", note: "Balanced quality and cost" },
    { id: "claude-opus-4-7", note: "Best reasoning, highest cost" },
  ],
};

// Pricing in USD per 1M tokens (input / output). Approximate as of mid-2025.
const PRICING: Record<string, { input: number; output: number }> = {
  "gpt-5.4-nano": { input: 0.2, output: 1.25 },
  "gpt-5.4-mini": { input: 0.75, output: 4.5 },
  "gpt-5.4": { input: 2.5, output: 15.0 },
  "gemini-2.5-flash-lite": { input: 0.1, output: 0.4 },
  "gemini-2.5-flash": { input: 0.3, output: 2.5 },
  "gemini-2.5-pro": { input: 1.25, output: 10.0 },
  "claude-haiku-4-5-20251001": { input: 1.0, output: 5.0 },
  "claude-sonnet-4-6": { input: 3.0, output: 15.0 },
  "claude-opus-4-7": { input: 5.0, output: 25.0 },
};

const ESTIMATED_OUTPUT_TOKENS = 400;

function estimateCost(modelId: string, promptText: string): string | null {
  const price = PRICING[modelId];
  if (!price) return null;
  const inputTokens = Math.ceil(promptText.length / 4);
  const cost =
    (inputTokens / 1_000_000) * price.input +
    (ESTIMATED_OUTPUT_TOKENS / 1_000_000) * price.output;
  if (cost < 0.0001) return "<$0.0001";
  return `~$${cost.toFixed(4)}`;
}

const PROVIDER_LABELS: Record<Provider, string> = {
  openai: "OpenAI",
  google: "Google",
  anthropic: "Anthropic",
};

const PERIOD_LABELS: Record<string, string> = {
  today: "Today",
  yesterday: "Yesterday",
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
};

const NEW_STATS_VIEWS: [string, string, string[] | null][] = [
  [
    "period",
    "Period summary",
    [
      "today",
      "yesterday",
      "this_week",
      "last_week",
      "this_month",
      "last_month",
    ],
  ],
  ["daily_count", "Daily counts", ["today", "yesterday"]],
  [
    "by_template",
    "Templates",
    ["this_week", "last_week", "this_month", "last_month"],
  ],
  ["by_campaign", "Campaigns", null],
];

const NEW_STATS_OPTIONS = NEW_STATS_VIEWS.flatMap(([view, label, periods]) =>
  periods === null
    ? [{ value: view, label, params: { view } }]
    : periods.map((p) => ({
        value: `${view}:${p}`,
        label: `${label} — ${PERIOD_LABELS[p]}`,
        params: { view, time_period: p },
      })),
);

const AdminAIPlan = () => {
  const { getToken } = useAuth();
  const [message, severity, setAlert, closeAlert] = useAlert();

  const [orgUsers, setOrgUsers] = useState<OrganizationUser[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<OrganizationUser | null>(null);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const isFirstRender = useRef(true);

  const [provider, setProvider] = useState<Provider>("openai");
  const [model, setModel] = useState<string>(MODEL_OPTIONS.openai[0].id);
  const [period, setPeriod] = useState(1);

  const [prompt, setPrompt] = useState("");
  const [stats, setStats] = useState<Stats | null>(null);
  const [result, setResult] = useState<PlanStep[] | null>(null);
  const [resultRaw, setResultRaw] = useState("");

  const [subscription, setSubscription] = useState<{
    planName: string | null;
    status: string;
    cancelAt: number | null;
  } | null>(null);

  const [fetchingPrompt, setFetchingPrompt] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [newStatsCombo, setNewStatsCombo] = useState<string>(
    NEW_STATS_OPTIONS[0].value,
  );
  const [newStatsResult, setNewStatsResult] = useState<unknown>(null);
  const [fetchingNewStats, setFetchingNewStats] = useState(false);

  const fetchOrgUsers = async (query?: string) => {
    setLoadingOrgs(true);
    try {
      const { organizationUsers } = await fetcherAuth(
        getToken,
        query
          ? `/api/admin/organization-users?query=${query}`
          : "/api/admin/organization-users",
        "GET",
      );
      setOrgUsers(organizationUsers);
    } catch (error) {
      setAlert("Failed to fetch organizations", "warning");
    }
    setLoadingOrgs(false);
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      fetchOrgUsers();
      return;
    }
    const timer = setTimeout(() => {
      fetchOrgUsers(inputValue || undefined);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    if (!selectedOrg) {
      setSubscription(null);
      return;
    }
    fetcherAuth(
      getToken,
      `/api/admin/organization-users/${selectedOrg.id}/subscription`,
      "GET",
    )
      .then(({ subscription: sub }) => setSubscription(sub))
      .catch(() => setSubscription(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  const handleProviderChange = (e: SelectChangeEvent) => {
    const newProvider = e.target.value as Provider;
    setProvider(newProvider);
    setModel(MODEL_OPTIONS[newProvider][0].id);
  };

  const handleGetPrompt = async () => {
    if (!selectedOrg) return;
    setFetchingPrompt(true);
    setResult(null);
    setResultRaw("");
    try {
      const { prompt: fetchedPrompt, stats: fetchedStats } = await fetcherAuth(
        getToken,
        `/api/admin/ai-plan/organizations/${selectedOrg.organizationId}/prompt?period=${period}`,
        "GET",
      );
      setPrompt(fetchedPrompt);
      setStats(fetchedStats);
    } catch (error) {
      setAlert("Failed to fetch prompt", "error");
    }
    setFetchingPrompt(false);
  };

  const handleFetchNewStats = async () => {
    if (!selectedOrg) return;
    const opt = NEW_STATS_OPTIONS.find((o) => o.value === newStatsCombo);
    if (!opt) return;
    setFetchingNewStats(true);
    setNewStatsResult(null);
    try {
      const params = new URLSearchParams(opt.params);
      const result = await fetcherAuth(
        getToken,
        `/api/admin/ai-plan/organizations/${selectedOrg.organizationId}/new-stats?${params}`,
        "GET",
      );
      setNewStatsResult(result);
    } catch (error) {
      setAlert("Failed to fetch new stats", "error");
    }
    setFetchingNewStats(false);
  };

  const handleGenerate = async () => {
    if (!selectedOrg || !prompt) return;
    setSubmitting(true);
    setResult(null);
    setResultRaw("");
    try {
      const { result: aiResult } = await fetcherAuth(
        getToken,
        `/api/admin/ai-plan/organizations/${selectedOrg.organizationId}/generate`,
        "POST",
        {},
        { provider, model, prompt },
      );
      setResultRaw(aiResult);
      try {
        setResult(JSON.parse(aiResult));
      } catch {
        setResult(null);
      }
    } catch (error) {
      setAlert("Failed to generate plan", "error");
    }
    setSubmitting(false);
  };

  return (
    <StyledPage
      content={
        <Grid sx={{ mt: 2, maxWidth: 900 }}>
          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />
          <Typography variant="h4" gutterBottom>
            <strong>AI Plan Advisor</strong>
          </Typography>

          <Autocomplete
            disablePortal
            loading={loadingOrgs}
            options={orgUsers}
            sx={{ width: 800, mt: 2 }}
            getOptionLabel={(option) =>
              `${option.name} - ${option.email} | ${option.organizationId}`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search organization"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingOrgs ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            onChange={(_event: any, newValue: OrganizationUser | null) => {
              setSelectedOrg(newValue);
              setPrompt("");
              setStats(null);
              setResult(null);
              setResultRaw("");
            }}
            onInputChange={(event: CustomEvent) => {
              setInputValue(event.target.value);
            }}
          />

          {selectedOrg && (
            <Box
              sx={{ display: "flex", gap: 1, mt: 1.5, alignItems: "center" }}
            >
              {subscription ? (
                <>
                  <Chip
                    label={subscription.planName ?? "free"}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={subscription.status}
                    size="small"
                    color={
                      subscription.status === "active"
                        ? "success"
                        : subscription.status === "past_due"
                          ? "warning"
                          : subscription.status === "canceled"
                            ? "error"
                            : "default"
                    }
                  />
                  {subscription.cancelAt && (
                    <Typography variant="caption" color="text.secondary">
                      cancels{" "}
                      {new Date(
                        subscription.cancelAt * 1000,
                      ).toLocaleDateString()}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No subscription
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2, mt: 3, alignItems: "center" }}>
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={period}
                label="Period"
                onChange={(e) => {
                  setPeriod(Number(e.target.value));
                  setPrompt("");
                  setStats(null);
                  setResult(null);
                  setResultRaw("");
                }}
              >
                {PERIOD_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <LoadingButton
              variant="outlined"
              loading={fetchingPrompt}
              disabled={!selectedOrg}
              onClick={handleGetPrompt}
            >
              Get Prompt
            </LoadingButton>
          </Box>

          {stats && (
            <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
              >
                <Typography variant="subtitle1">
                  <strong>{stats.period}</strong>
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 2 }}>
                {(
                  [
                    ["Outreach sent", stats.activity.outreachEmailsSent],
                    stats.monthlyLimit !== null
                      ? ["Monthly limit", stats.monthlyLimit]
                      : null,
                    ["Follow-ups sent", stats.activity.followUpEmailsSent],
                    ["Open rate", `${stats.activity.openRate}%`],
                    ["Response rate", `${stats.activity.responseRate}%`],
                    ["Responses", stats.activity.responsesReceived],
                    ["Positive", stats.activity.positiveResponses],
                    ["Negative", stats.activity.negativeResponses],
                    ["Neutral", stats.activity.neutralResponses],
                    [
                      "Follow-up steps",
                      stats.maxFollowupSteps === 0
                        ? "none"
                        : stats.maxFollowupSteps,
                    ],
                    ["Outreach templates", stats.outreachTemplateCount],
                    ["Pending responses", stats.pendingBrandResponseCount],
                    ["Rate discussions", stats.pendingRateDiscussionCount],
                    ["Total action items", stats.totalPendingActionItems],
                  ] as ([string, string | number] | null)[]
                )
                  .filter(
                    (item): item is [string, string | number] => item !== null,
                  )
                  .map(([label, value]) => (
                    <Box key={label}>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{value}</strong>
                      </Typography>
                    </Box>
                  ))}
              </Box>

              {stats.templates.length > 0 && (
                <>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="caption" color="text.secondary">
                    Templates
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}
                  >
                    {stats.templates.map((t) => (
                      <Chip
                        key={t.name}
                        label={[
                          t.name,
                          `${t.sent} sent`,
                          `${t.response_rate}% response`,
                          t.days_since_update !== null
                            ? `updated ${t.days_since_update}d ago`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </>
              )}

              {stats.campaigns.length > 0 && (
                <>
                  <Divider sx={{ mt: 1.5, mb: 1.5 }} />
                  <Typography variant="caption" color="text.secondary">
                    Campaigns
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}
                  >
                    {stats.campaigns.map((c) => {
                      const dates = c.start_date
                        ? `${c.start_date}${c.end_date ? ` – ${c.end_date}` : ""}`
                        : null;
                      const label = [
                        c.name,
                        c.status,
                        dates,
                        `${c.emails_sent} sent`,
                      ]
                        .filter(Boolean)
                        .join(" · ");
                      return (
                        <Chip
                          key={c.name}
                          label={label}
                          size="small"
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>
                </>
              )}
            </Paper>
          )}

          {selectedOrg && (
            <>
              <Box
                sx={{ display: "flex", gap: 2, mt: 3, alignItems: "center" }}
              >
                <FormControl sx={{ minWidth: 320 }}>
                  <InputLabel>New stats path</InputLabel>
                  <Select
                    value={newStatsCombo}
                    label="New stats path"
                    onChange={(e) => setNewStatsCombo(e.target.value)}
                  >
                    {NEW_STATS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <LoadingButton
                  variant="outlined"
                  loading={fetchingNewStats}
                  onClick={handleFetchNewStats}
                >
                  Fetch new stats
                </LoadingButton>
              </Box>
              {newStatsResult !== null && (
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 2,
                    p: 2,
                    fontFamily: "monospace",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {JSON.stringify(newStatsResult, null, 2)}
                </Paper>
              )}
            </>
          )}

          {prompt !== "" && (
            <Box sx={{ mt: 3 }}>
              <TextField
                label="Prompt"
                multiline
                minRows={10}
                fullWidth
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />

              <Box
                sx={{ display: "flex", gap: 2, mt: 2, alignItems: "center" }}
              >
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={provider}
                    label="Provider"
                    onChange={handleProviderChange}
                  >
                    {(Object.keys(MODEL_OPTIONS) as Provider[]).map((p) => (
                      <MenuItem key={p} value={p}>
                        {PROVIDER_LABELS[p]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 320 }}>
                  <InputLabel>Model</InputLabel>
                  <Select
                    value={model}
                    label="Model"
                    onChange={(e) => setModel(e.target.value)}
                  >
                    {MODEL_OPTIONS[provider].map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        <Box>
                          <Typography variant="body2">{m.id}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {m.note}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <LoadingButton
                  variant="contained"
                  loading={submitting}
                  onClick={handleGenerate}
                >
                  Generate Plan
                </LoadingButton>

                {prompt && (
                  <Typography variant="caption" color="text.secondary">
                    {estimateCost(model, prompt) ?? "pricing unknown"}
                    {" est."}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {(result || resultRaw) && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {PROVIDER_LABELS[provider]} / {model}
              </Typography>
              {result ? (
                result.map((step, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 1.5,
                      display: "flex",
                      gap: 2,
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        minWidth: 20,
                        color: "text.secondary",
                      }}
                    >
                      {i + 1}.
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {step.action}
                        </Typography>
                        <Chip
                          label={step.type}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {step.detail}
                      </Typography>
                    </Box>
                  </Paper>
                ))
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    Could not parse response as JSON.
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
                  >
                    {resultRaw}
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </Grid>
      }
    />
  );
};

export default AdminAIPlan;

export { AdminAIPlan };
